import Box from '@mui/material/Box';
import type { inferOutput } from '@trpc/tanstack-react-query';
import type { ReactNode } from 'react';
import { formatCompactCurrency, formatPercent } from '../../lib/format';
import type { useTRPC } from '../../lib/trpc';
import { CreativeHealthCard } from './CreativeHealthCard';
import { HERO_GRID_SX } from './layout';
import { MetricTile } from './MetricTile';
import { TooltipTrackingProvider } from './TooltipTracking';
import { TOOLTIPS } from './tooltips';
import { WhereIsTheMoneyCard } from './WhereIsTheMoneyCard';

type TRPC = ReturnType<typeof useTRPC>;
export type HeroMetricsData = inferOutput<TRPC['dashboard']['heroMetrics']>;

export function HeroMetrics({ data }: { data: HeroMetricsData }): ReactNode {
  const symbol = data.currencySymbol;
  // Nothing ran in this scope (or no analytics are loaded): dashes and a reason, not zeroes.
  const noData = data.totalSpend.value === 0 && data.totalSpend.activeCreatives === 0;
  const dash = (value: string): string => (noData ? '—' : value);

  return (
    <TooltipTrackingProvider>
      <Box sx={HERO_GRID_SX} data-tour="hero" data-testid="hero-metrics" data-no-data={noData}>
        <CreativeHealthCard data={data.creativeHealth} />
        <WhereIsTheMoneyCard data={data.whereIsTheMoney} currencySymbol={symbol} noData={noData} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MetricTile
            title="Total Spend"
            tip={TOOLTIPS.totalSpend}
            value={dash(formatCompactCurrency(data.totalSpend.value, symbol))}
            caption={
              noData
                ? 'No spend recorded for this scope and date range'
                : `${data.totalSpend.activeCreatives} creatives ran`
            }
            delta={noData ? null : data.totalSpend.delta}
          />
          <MetricTile
            title="Winners Rate"
            tip={TOOLTIPS.winnersRate}
            value={dash(formatPercent(data.winnersRate.rate, 0))}
            caption={
              noData
                ? 'No creatives to compare'
                : `${data.winnersRate.count} in ${data.winnersRate.total} creatives outperformed`
            }
            delta={noData ? null : data.winnersRate.delta}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MetricTile
            title="Recoverable Budget"
            tip={TOOLTIPS.recoverableBudget}
            value={dash(formatCompactCurrency(data.recoverableBudget.value, symbol))}
            caption="Projected waste over the next 30 days if nothing changes"
            delta={noData ? null : data.recoverableBudget.delta}
            invertDelta
          />
          <MetricTile
            title="Loser Rate"
            tip={TOOLTIPS.losersRate}
            value={dash(formatPercent(data.losersRate.rate, 0))}
            caption={
              noData
                ? 'No creatives to compare'
                : `${data.losersRate.count} in ${data.losersRate.total} creatives underperformed`
            }
            delta={noData ? null : data.losersRate.delta}
            invertDelta
          />
        </Box>
      </Box>
    </TooltipTrackingProvider>
  );
}
